// Large-footer link map (Framer-style multi-column). Declarative so columns are
// editable in one place. Slugs MUST match the marketing content registry
// (content/marketing-pages.tsx) and the route folders under app/(marketing).

import type { ComponentType } from 'react';

import { INTEGRATIONS_CELL, PRODUCT_STAGES, stageHref } from '../content/product/product-stages';
import { RESOURCES_ITEMS } from './marketing-nav';

export interface FooterLink {
	label: string;
	href: string;
	icon?: ComponentType<{ className?: string }>;
	/** Off-site destination - rendered as a plain anchor opening in a new tab. */
	external?: boolean;
}

export interface FooterColumn {
	title: string;
	links: readonly FooterLink[];
}

// The columns mirror the nav taxonomy (LANDING_INSPIRATION G1/G3) and are
// DERIVED from the same registries the popovers read - the Product column from
// PRODUCT_STAGES (per that file's one-registry contract) + Integrations +
// Pricing, the Resources column from RESOURCES_ITEMS - so the footer and a
// popover can never disagree about a page. Security lives in Resources (via
// RESOURCES_ITEMS), not the Product column. The old Compare column dissolved
// into Resources' "Switching from" (/compare index carries the per-tool links).
export const FOOTER_COLUMNS: readonly FooterColumn[] = [
	{
		title: 'Product',
		links: [
			// Plain stage names - the "01" ledger numbers stay on the popover/hub
			// surfaces but are deliberately dropped in the footer (user ruling).
			...PRODUCT_STAGES.map((stage) => ({
				label: stage.label,
				href: stageHref(stage),
			})),
			{ label: INTEGRATIONS_CELL.label, href: INTEGRATIONS_CELL.href },
			{ label: 'Pricing', href: '/pricing' },
			{ label: 'Services', href: '/services' },
			{ label: 'Partners', href: '/partners' },
		],
	},
	{
		title: 'Use cases',
		links: [
			{ label: 'Teams', href: '/use-cases/teams' },
			{ label: 'Sales & client teams', href: '/use-cases/sales-teams' },
			{ label: 'Agencies & consultancies', href: '/use-cases/agencies' },
			{ label: 'Researchers', href: '/use-cases/researcher' },
			{ label: 'Solo builders', href: '/use-cases/solo-builder' },
			{ label: 'Personal knowledge base', href: '/use-cases/personal-kb' },
		],
	},
	{
		title: 'Resources',
		// The Resources popover's registry verbatim (About, Blog, Docs, Security,
		// Switching from, Desktop app) - its group order is already the sane
		// reading order for a footer column - plus Agent skills, which left the
		// popover to keep its 3x2 grid full but stays linked from the footer.
		links: [
			...RESOURCES_ITEMS.map((item) => ({ label: item.label, href: item.href })),
			{ label: 'Agent skills', href: '/docs/agent-skills' },
		],
	},
] as const;

// The social row is shared with apps/web's sign-in shell, so it lives in
// `@naumu/ui/components/footer-social`. Re-exported here so the footer keeps
// importing every column set from one module.
export { FOOTER_SOCIAL } from '@naumu/ui/components/footer-social';

export const FOOTER_LEGAL: readonly FooterLink[] = [
	{ label: 'Privacy', href: '/legal/privacy-policy' },
	{ label: 'Terms', href: '/legal/terms-of-service' },
	{ label: 'Cookies', href: '/legal/cookie-policy' },
	{
		label: 'Trustpilot',
		href: 'https://www.trustpilot.com/review/naumu.ai',
		external: true,
	},
] as const;
